import { useEffect, useState } from "react";
import {
  Search, Plus, FileText, Printer, Download, UserRound,
  Receipt, CreditCard, X
} from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { inr, fmtDate, fmtDateTime } from "@/lib/utils";

export default function AdminBilling() {
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [billType, setBillType] = useState("Non-GST");
  const [busy, setBusy] = useState(false);
  const [business, setBusiness] = useState(null);
  const [settings, setSettings] = useState(null);
  const [showBill, setShowBill] = useState(false);

  const load = async () => {
    try {
      setBusy(true);
      const [ordersRes, businessRes, settingsRes] = await Promise.all([
        api.get("/admin/orders"),
        api.get("/public/business"),
        api.get("/admin/settings"),
      ]);
      setOrders(ordersRes.data || []);
      setBusiness(businessRes.data || {});
      setSettings(settingsRes.data || {});
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = orders.filter((o) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [
      o.order_number,
      o.customer?.name,
      o.customer?.mobile,
      o.customer?.license_number,
      o.customer?.pos_id,
    ].some((v) => String(v || "").toLowerCase().includes(q));
  });

  const createBill = async () => {
    if (!selected) return;

    try {
      setBusy(true);
      const { data } = await api.post("/admin/billing/create", {
        order_id: selected.id,
        bill_type: billType,
      });
      setSelected({ ...selected, bill: data });
      setShowBill(true);
      toast.success("Bill " + data.bill_number + " created");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const openExistingBill = () => {
    if (!selected) return;
    setShowBill(true);
  };

  const printBill = () => {
    window.print();
  };

  const total = Number(selected?.payment?.total || 0);
  const paid = Number(selected?.payment?.paid || 0);
  const due = Number(selected?.payment?.due || 0);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">
            Billing & Transactions
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Create, manage and print wholesale fertilizer bills.
          </p>
        </div>

        <button
          className="sf-btn-primary inline-flex items-center gap-2"
          onClick={() => {
            setSelected(null);
            setShowBill(false);
          }}
        >
          <Plus className="h-4 w-4" />
          New Bill
        </button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[430px_1fr] gap-5">
        <div className="sf-card overflow-hidden">
          <div className="p-4 border-b border-slate-200">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <input
                className="sf-input pl-9 w-full"
                placeholder="Search customer, invoice, mobile, licence or POS ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="p-3 border-b border-slate-200 flex gap-2">
            <select
              className="sf-input flex-1"
              value={billType}
              onChange={(e) => setBillType(e.target.value)}
            >
              <option>Non-GST</option>
              <option>GST</option>
              <option>Receipt</option>
            </select>
            <button
              className="sf-btn-primary"
              disabled={!selected || busy}
              onClick={createBill}
            >
              <Receipt className="h-4 w-4 inline mr-1" />
              Create
            </button>
          </div>

          <div className="max-h-[720px] overflow-auto">
            {busy && !orders.length && (
              <div className="p-5 text-sm text-slate-500">
                Loading transactions...
              </div>
            )}

            {!busy && !filtered.length && (
              <div className="p-8 text-center text-sm text-slate-400">
                No transactions found.
              </div>
            )}

            {filtered.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => {
                  setSelected(o);
                  setShowBill(false);
                }}
                className={`w-full text-left p-4 border-b border-slate-100 hover:bg-slate-50 transition ${
                  selected?.id === o.id
                    ? "bg-emerald-50 border-l-4 border-l-emerald-500"
                    : ""
                }`}
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="font-bold text-slate-900">
                    {o.order_number}
                  </div>
                  <div className="text-xs text-slate-400">
                    {fmtDate(o.created_at)}
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-2">
                  <UserRound className="h-4 w-4 text-slate-400" />
                  <span className="font-semibold text-sm">
                    {o.customer?.name || "Customer"}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-slate-500">
                  <span>Mobile: {o.customer?.mobile || "—"}</span>
                  <span>Licence: {o.customer?.license_number || "—"}</span>
                </div>

                <div className="flex justify-between mt-3 text-xs">
                  <span className="text-slate-500">{o.status}</span>
                  <span className="font-bold text-slate-800">
                    {inr(o.payment?.total || 0)}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="sf-card p-5">
          {!selected ? (
            <div className="min-h-[620px] flex flex-col items-center justify-center text-slate-400">
              <Receipt className="h-14 w-14 mb-4" />
              <div className="font-display font-bold text-lg text-slate-500">
                Select a transaction
              </div>
              <div className="text-sm mt-1">
                Choose a customer/order from the transaction register.
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex flex-wrap justify-between items-start gap-3">
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-400">
                    Transaction
                  </div>
                  <h2 className="font-display text-xl font-bold text-slate-900">
                    {selected.order_number}
                  </h2>
                  <div className="text-sm text-slate-500">
                    {fmtDateTime(selected.created_at)}
                  </div>
                </div>

                <div className="flex gap-2">
                  {selected.bill && (
                    <button
                      className="sf-btn-primary inline-flex items-center gap-2"
                      onClick={openExistingBill}
                    >
                      <FileText className="h-4 w-4" />
                      View Bill
                    </button>
                  )}
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center gap-2 font-bold text-slate-900 mb-3">
                    <UserRound className="h-4 w-4" />
                    Customer Details
                  </div>

                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="sf-label">Customer Name</span>
                      <div className="font-semibold">
                        {selected.customer?.name || "—"}
                      </div>
                    </div>

                    <div>
                      <span className="sf-label">Mobile</span>
                      <div className="font-semibold">
                        {selected.customer?.mobile || "—"}
                      </div>
                    </div>

                    <div>
                      <span className="sf-label">Licence Number</span>
                      <div className="font-semibold">
                        {selected.customer?.license_number || "—"}
                      </div>
                    </div>

                    <div>
                      <span className="sf-label">POS ID</span>
                      <div className="font-semibold">
                        {selected.customer?.pos_id || "—"}
                      </div>
                    </div>

                    <div>
                      <span className="sf-label">Address</span>
                      <div className="font-semibold whitespace-pre-line">
                        {selected.customer?.address || "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center gap-2 font-bold text-slate-900 mb-3">
                    <CreditCard className="h-4 w-4" />
                    Payment Summary
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div className="rounded-lg bg-white border p-3">
                      <div className="sf-label">Total</div>
                      <div className="font-bold mt-1">{inr(total)}</div>
                    </div>
                    <div className="rounded-lg bg-white border p-3">
                      <div className="sf-label">Paid</div>
                      <div className="font-bold text-emerald-700 mt-1">
                        {inr(paid)}
                      </div>
                    </div>
                    <div className="rounded-lg bg-white border p-3">
                      <div className="sf-label">Due</div>
                      <div className="font-bold text-rose-700 mt-1">
                        {inr(due)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="sf-card overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 font-display font-bold">
                  Fertilizer Items
                </div>

                <div className="overflow-auto">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="sf-th">Sl.</th>
                        <th className="sf-th">Company</th>
                        <th className="sf-th">Product</th>
                        <th className="sf-th">Qty</th>
                        <th className="sf-th">Rate / MT</th>
                        <th className="sf-th text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selected.items || []).map((item, i) => (
                        <tr key={item.id || i}>
                          <td className="sf-td">{i + 1}</td>
                          <td className="sf-td">{item.company_name || "—"}</td>
                          <td className="sf-td font-semibold">
                            {item.product_name || "—"}
                          </td>
                          <td className="sf-td">
                            {item.approved_qty ?? item.requested_qty}{" "}
                            {item.approved_unit || item.requested_unit}
                          </td>
                          <td className="sf-td">
                            {item.rate_per_mt != null
                              ? inr(item.rate_per_mt)
                              : "Call for Rate"}
                          </td>
                          <td className="sf-td text-right font-semibold">
                            {item.line_total != null
                              ? inr(item.line_total)
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end">
                <div className="w-full sm:w-80 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Total</span>
                    <b>{inr(total)}</b>
                  </div>
                  <div className="flex justify-between">
                    <span>Paid</span>
                    <b className="text-emerald-700">{inr(paid)}</b>
                  </div>
                  <div className="flex justify-between border-t pt-2 text-base">
                    <span>Balance Due</span>
                    <b className="text-rose-700">{inr(due)}</b>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showBill && selected && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-auto">
            <div className="sticky top-0 z-10 bg-white border-b px-5 py-3 flex items-center justify-between">
              <div className="font-display font-bold">Invoice Preview</div>
              <div className="flex gap-2">
                <button className="sf-btn-primary inline-flex items-center gap-2" onClick={printBill}>
                  <Printer className="h-4 w-4" />
                  Print
                </button>
                <button
                  className="sf-btn-secondary inline-flex items-center gap-2"
                  onClick={() => setShowBill(false)}
                >
                  <X className="h-4 w-4" />
                  Close
                </button>
              </div>
            </div>

            <div id="billing-document" className="p-8">
              <div className="border-2 border-slate-800">
                <div className="p-5 border-b-2 border-slate-800 flex justify-between gap-5">
                  <div>
                    {business?.logo_url && (
                      <img
                        src={business.logo_url}
                        alt="Business logo"
                        className="h-20 w-20 object-contain mb-2"
                      />
                    )}
                    <h1 className="text-2xl font-bold">
                      {business?.business_name || "M/S SABUJ SONA FERTILIZER"}
                    </h1>
                    {settings?.pdf_show_business_address !== false && (
                      <div className="text-sm mt-1 leading-5">
                        {business?.stall_no && <>{business.stall_no}<br /></>}
                        {business?.landmark && <>{business.landmark}<br /></>}
                        {[
                          business?.district,
                          business?.state
                        ].filter(Boolean).join(", ")}
                        {(business?.pin || business?.country) && (
                          <> - {[business?.pin, business?.country].filter(Boolean).join(", ")}</>
                        )}
                      </div>
                    )}
                    {settings?.pdf_show_license_no !== false && business?.license_no && (
                      <div className="text-sm mt-1">
                        Fertilizer Licence / Authorization No.: {business.license_no}
                      </div>
                    )}
                    {settings?.pdf_show_mobile !== false && (
                      <div className="text-sm mt-1">
                        Mobile: {settings?.pdf_mobile_numbers || business?.contact_numbers?.join(", ") || "—"}
                      </div>
                    )}
                    {billType === "GST" && settings?.pdf_show_gstin !== false && business?.gstin && (
                      <div className="text-sm font-semibold mt-1">
                        GSTIN: {business.gstin}
                      </div>
                    )}
                  </div>

                  <div className="text-right">
                    <div className="text-xs uppercase tracking-wide">
                      {billType}
                    </div>
                    <div className="text-2xl font-bold mt-1">
                      {selected.bill?.bill_number || "Draft"}
                    </div>
                    <div className="text-sm mt-2">
                      Date: {fmtDate(selected.created_at)}
                    </div>
                    <div className="text-sm">
                      Order: {selected.order_number}
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 border-b-2 border-slate-800">
                  <div className="p-4 border-r md:border-r-2 border-slate-800">
                    <div className="font-bold mb-2">Bill To</div>
                    <div className="font-semibold">
                      {selected.customer?.name || "—"}
                    </div>
                    <div>{selected.customer?.address || "—"}</div>
                    <div>Mobile: {selected.customer?.mobile || "—"}</div>
                    <div>
                      Licence No: {selected.customer?.license_number || "—"}
                    </div>
                    <div>POS ID: {selected.customer?.pos_id || "—"}</div>
                  </div>

                  <div className="p-4">
                    <div className="font-bold mb-2">Payment</div>
                    <div>Method: {selected.payment?.method || "—"}</div>
                    <div>Status: {selected.payment?.status || "—"}</div>
                    <div>Paid: {inr(paid)}</div>
                    <div>Balance: {inr(due)}</div>
                  </div>
                </div>

                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="border p-2">Sl.</th>
                      <th className="border p-2 text-left">Product</th>
                      <th className="border p-2 text-left">Company</th>
                      <th className="border p-2 text-right">Qty</th>
                      <th className="border p-2 text-right">Rate</th>
                      <th className="border p-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selected.items || []).map((item, i) => (
                      <tr key={item.id || i}>
                        <td className="border p-2 text-center">{i + 1}</td>
                        <td className="border p-2">{item.product_name}</td>
                        <td className="border p-2">{item.company_name}</td>
                        <td className="border p-2 text-right">
                          {item.approved_qty ?? item.requested_qty}{" "}
                          {item.approved_unit || item.requested_unit}
                        </td>
                        <td className="border p-2 text-right">
                          {item.rate_per_mt != null
                            ? inr(item.rate_per_mt)
                            : "Call for Rate"}
                        </td>
                        <td className="border p-2 text-right">
                          {item.line_total != null
                            ? inr(item.line_total)
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="p-5 border-t-2 border-slate-800">
                  <div className="ml-auto max-w-sm space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Total</span>
                      <b>{inr(total)}</b>
                    </div>
                    <div className="flex justify-between">
                      <span>Paid</span>
                      <b>{inr(paid)}</b>
                    </div>
                    <div className="flex justify-between border-t pt-2 text-lg">
                      <span>Balance Due</span>
                      <b>{inr(due)}</b>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 p-5 border-t-2 border-slate-800 text-sm">
                  <div className="rounded-lg border border-slate-300 p-3">
                    <div className="font-bold mb-2">Payment Details</div>
                    {settings?.pdf_show_bank_details !== false && (
                      <div className="space-y-0.5">
                        {settings?.bank_holder_name && <div>Account Holder: {settings.bank_holder_name}</div>}
                        {settings?.bank_name && <div>Bank: {settings.bank_name}</div>}
                        {settings?.bank_account_number && <div>Account No.: {settings.bank_account_number}</div>}
                        {settings?.bank_ifsc && <div>IFSC: {settings.bank_ifsc}</div>}
                        {settings?.bank_branch && <div>Branch: {settings.bank_branch}</div>}
                      </div>
                    )}
                    {settings?.pdf_show_upi_id !== false && settings?.upi_id && (
                      <div className="mt-2">
                        <span className="font-semibold">UPI:</span> {settings.upi_id}
                        {settings?.upi_display_name && (
                          <span> ({settings.upi_display_name})</span>
                        )}
                      </div>
                    )}
                  </div>

                  {settings?.pdf_show_qr !== false && settings?.payment_qr_url && (
                    <div className="rounded-lg border border-slate-300 p-3 flex items-center justify-center gap-4">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${settings.payment_qr_url}`}
                        alt="Payment QR"
                        className="h-24 w-24 object-contain"
                      />
                      <div className="text-xs text-slate-600">
                        <div className="font-bold text-slate-900">Scan to Pay</div>
                        <div className="mt-1">Use the QR code for digital payment.</div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-6 p-5 text-sm">
                  <div>
                    <div className="font-bold mb-3">Stamp</div>
                    {settings?.pdf_show_stamp !== false && settings?.pdf_stamp_mode !== "none" ? (
                      settings?.pdf_stamp_mode === "online" && settings?.invoice_stamp_url ? (
                        <div className="h-20 flex items-center justify-center">
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${settings.invoice_stamp_url}`}
                            alt="Official Stamp"
                            className="max-h-20 max-w-40 object-contain"
                          />
                        </div>
                      ) : (
                        <div className="h-20 border border-dashed border-slate-400 rounded-lg flex items-center justify-center text-xs text-slate-500">
                          Stamp
                        </div>
                      )
                    ) : null}
                  </div>

                  <div>
                    <div className="font-bold mb-3 text-right">Authorized Signature</div>
                    {settings?.pdf_show_signature !== false && settings?.pdf_signature_mode !== "none" ? (
                      settings?.pdf_signature_mode === "online" && settings?.invoice_signature_url ? (
                        <div className="h-20 flex items-center justify-end">
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${settings.invoice_signature_url}`}
                            alt="Authorized Signature"
                            className="max-h-16 max-w-48 object-contain"
                          />
                        </div>
                      ) : (
                        <div className="h-20 border border-dashed border-slate-400 rounded-lg flex items-end justify-end pb-2 px-3 text-xs text-slate-500">
                          Signature
                        </div>
                      )
                    ) : null}
                  </div>
                </div>

                <div className="p-4 border-t text-xs text-slate-500 text-center">
                  Computer generated document — {business?.business_name || "Sabuj Sona Fertilizer"}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
